import { useEffect, useState } from "react";
import { Avatar } from "@mantine/core";
import { prefix, getAuthHeaders } from "../../../Boilerplate/api";

// Process-wide cache so a company's logo is fetched ONCE and the SAME object URL is shared by every
// <CompanyLogo> on the page — the same employer recurs across many candidates' career timelines.
// Keyed by company id. A 404 (no logo stored) is cached as null so we don't refetch a known-missing
// logo on every render. Object URLs live for the session (siblings may still reference them).
const logoUrlCache = new Map(); // companyId -> object URL | null
const logoInflight = new Map(); // companyId -> Promise<string|null>

function loadLogo(companyId) {
  if (logoUrlCache.has(companyId)) return Promise.resolve(logoUrlCache.get(companyId));
  if (logoInflight.has(companyId)) return logoInflight.get(companyId);
  const p = fetch(`${prefix}/companies/${companyId}/logo`, { headers: getAuthHeaders() })
    .then((r) => (r.ok ? r.blob() : null))
    .then((b) => {
      const url = b ? URL.createObjectURL(b) : null;
      logoUrlCache.set(companyId, url); // cache null too (known-missing)
      return url;
    })
    .catch(() => null)
    .finally(() => logoInflight.delete(companyId));
  logoInflight.set(companyId, p);
  return p;
}

function initials(name) {
  return name?.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

/**
 * Square-ish logo for an employer. Fetches /companies/{id}/logo with auth headers and shows it; falls
 * back to the company's initials when there's no logo (or no resolved company id yet). Deduped + cached
 * across the whole page.
 */
export function CompanyLogo({ companyId, companyName, ...avatarProps }) {
  const [url, setUrl] = useState(() => (companyId ? logoUrlCache.get(companyId) ?? null : null));
  useEffect(() => {
    if (!companyId) { setUrl(null); return undefined; }
    const cached = logoUrlCache.get(companyId);
    if (cached !== undefined) { setUrl(cached); return undefined; }
    let cancelled = false;
    loadLogo(companyId).then((u) => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, [companyId]);

  return (
    <Avatar src={url} radius="sm" {...avatarProps}>
      {initials(companyName)}
    </Avatar>
  );
}
